import { HighlightShape } from 'types/highlight';
import { HIGHLIGHT_CUTOUT_PADDING } from 'utils/highlight';

const CUTOUT_PADDING = HIGHLIGHT_CUTOUT_PADDING;
const MIN_CUTOUT_RADIUS = 12;
const DIM_FILL = 'rgba(15, 23, 42, 0.45)';
const RING_COLOR = 'rgba(255, 255, 255, 0.9)';
const MASK_ID = 'tips-dim-overlay-mask';
const CLIP_ID_PREFIX = 'tips-dim-overlay-clip-';

type TipsDimOverlayProps = {
  readonly shapes: HighlightShape[];
};

type ShapeElementProps = {
  readonly shape: HighlightShape;
  readonly variant: 'cutout' | 'ring';
};

function ShapeElement({ shape, variant }: ShapeElementProps) {
  const { rect, polygonPoints, svgPath, borderRadius } = shape;
  const isCutout = variant === 'cutout';

  if (svgPath) {
    const scaleX = rect.width / svgPath.bbox.width;
    const scaleY = rect.height / svgPath.bbox.height;
    const tx = rect.left - svgPath.bbox.x * scaleX;
    const ty = rect.top - svgPath.bbox.y * scaleY;
    return (
      <g transform={`translate(${tx} ${ty}) scale(${scaleX} ${scaleY})`}>
        <path
          d={svgPath.d}
          vectorEffect='non-scaling-stroke'
          strokeLinejoin='round'
          fill={isCutout ? 'black' : 'none'}
          stroke={isCutout ? 'black' : RING_COLOR}
          strokeWidth={isCutout ? CUTOUT_PADDING * 2 : 2}
        />
      </g>
    );
  }

  if (polygonPoints) {
    return (
      <polygon
        points={polygonPoints
          .map((p) => `${rect.left + p.x},${rect.top + p.y}`)
          .join(' ')}
        strokeLinejoin='round'
        fill={isCutout ? 'black' : 'none'}
        stroke={isCutout ? 'black' : RING_COLOR}
        strokeWidth={isCutout ? CUTOUT_PADDING * 2 : 2}
      />
    );
  }

  const parsedRadius = parseFloat(borderRadius ?? '0') || 0;
  const radius = Math.max(parsedRadius + CUTOUT_PADDING, MIN_CUTOUT_RADIUS);

  return (
    <rect
      x={rect.left - CUTOUT_PADDING}
      y={rect.top - CUTOUT_PADDING}
      width={rect.width + CUTOUT_PADDING * 2}
      height={rect.height + CUTOUT_PADDING * 2}
      rx={radius}
      ry={radius}
      fill={isCutout ? 'black' : 'none'}
      stroke={isCutout ? undefined : RING_COLOR}
      strokeWidth={isCutout ? undefined : 2}
    />
  );
}

export default function TipsDimOverlay({ shapes }: TipsDimOverlayProps) {
  if (shapes.length === 0) {
    return null;
  }

  const width = window.innerWidth;
  const height = window.innerHeight;

  // Confine each shape to the visible area of its ancestor scroll containers
  // so highlights don't render over content that clips their target.
  const getClipPathUrl = (index: number) =>
    shapes[index].clipRect ? `url(#${CLIP_ID_PREFIX}${index})` : undefined;

  return (
    <svg
      aria-hidden='true'
      className='tips-fade-in pointer-events-none fixed inset-0'
      style={{ width, height, zIndex: 9998 }}
    >
      <defs>
        {shapes.map(({ clipRect }, index) =>
          clipRect ? (
            // eslint-disable-next-line react/no-array-index-key
            <clipPath key={index} id={`${CLIP_ID_PREFIX}${index}`}>
              <rect
                x={clipRect.left}
                y={clipRect.top}
                width={clipRect.width}
                height={clipRect.height}
              />
            </clipPath>
          ) : null
        )}
        <mask
          id={MASK_ID}
          maskUnits='userSpaceOnUse'
          x={0}
          y={0}
          width={width}
          height={height}
        >
          <rect x={0} y={0} width={width} height={height} fill='white' />
          {shapes.map((shape, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <g key={index} clipPath={getClipPathUrl(index)}>
              <ShapeElement shape={shape} variant='cutout' />
            </g>
          ))}
        </mask>
      </defs>
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill={DIM_FILL}
        mask={`url(#${MASK_ID})`}
      />
      {shapes.map((shape, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <g key={index} clipPath={getClipPathUrl(index)}>
          <ShapeElement shape={shape} variant='ring' />
        </g>
      ))}
    </svg>
  );
}
